import React, { useMemo, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { useOkapiKy } from '@folio/stripes-core';
import { useQuery } from 'react-query';
import { buildUrl } from '@folio/stripes/smart-components';
import TranslationWrapper from '../components/TranslationWrapper/TranslationWrapper';
import { buildCQL } from '../Hooks/useTranslationKeys';
import { useLanguages } from '../Hooks/useLanguages';
import { useLanguageTranslators } from '../Hooks/useLanguageTranslators';

const CATEGORY  = 'libTranslations';
const PAGE_SIZE = 50;

/**
 * Parses the filters string from URL e.g. "localeValue.ar,status.translated,status.notTranslated"
 * into { localeValue: ['ar'], status: ['translated', 'notTranslated'] }
 */
function parseFilters(filtersStr = '') {
  const map = {};
  filtersStr.split(',').filter(Boolean).forEach((f) => {
    const dot = f.indexOf('.');
    if (dot === -1) return;
    const name  = f.substring(0, dot);
    const value = f.substring(dot + 1);
    if (!map[name]) map[name] = [];
    map[name].push(value);
  });
  return map;
}

/**
 * Builds CQL from SearchAndSortQuery queryParams.
 * queryParams = { query, filters, sort, ... }
 */
function buildTranslationCQL(queryParams) {
  const filters = parseFilters(queryParams.filters);
  return buildCQL({
    categoryName: CATEGORY,
    localeValue:  filters.localeValue?.[0] || '',
    appName:      filters.appName          || [],
    tableName:    filters.tableName        || [],
    status:       filters.status           || [],
    query:        queryParams.query        || '',
  });
}

/**
 * LibTranslationsRoute — Container for libTranslations category.
 *
 * Follows ReportsListRoute pattern exactly:
 *  - querySetter/queryGetter manage URL state
 *  - SearchAndSortQuery (in TranslationWrapper) handles resetAll, clearGroup, etc.
 *  - useOkapiKy + react-query for data fetching
 */
function LibTranslationsRoute({ history, location }) {
  const intl = useIntl();
  const ky   = useOkapiKy();

  // ── querySetter / queryGetter ─────────────────────────────────────
  const queryGetter = () => {
    const p = new URLSearchParams(location.search);
    return {
      query:   p.get('query')   || '',
      filters: p.get('filters') || '',
      sort:    p.get('sort')    || 'translationKey',
      offset:  parseInt(p.get('offset') || '0', 10),
    };
  };

  const querySetter = ({ nsValues }) => {
    const url = buildUrl(location, nsValues);
    const { pathname, search } = location;
    if (`${pathname}${search}` !== url) history.push(url);
  };

  const q = queryGetter();
  const parsedFilters = parseFilters(q.filters);
  const localeValue   = parsedFilters.localeValue?.[0] || '';
  const appName       = parsedFilters.appName           || [];

  // ── Languages & Translators ───────────────────────────────────────
  const { data: langData }       = useLanguages();
  const languages                = langData?.languages || [];
  const { isAssignedTranslator } = useLanguageTranslators();

  // ── Set default locale on first visit ────────────────────────────
  const defaultSet = useRef(false);
  useEffect(() => {
    if (defaultSet.current || languages.length === 0 || localeValue) return;
    defaultSet.current = true;
    const def = languages.find((l) => l.localeValue === intl.locale)
      ? intl.locale
      : languages[0]?.localeValue;
    if (def) {
      const currentFilters = q.filters ? q.filters + `,localeValue.${def}` : `localeValue.${def}`;
      history.replace(buildUrl(location, { filters: currentFilters }));
    }
  }, [languages.length]);

  // ── CQL ───────────────────────────────────────────────────────────
  const cql = useMemo(() => buildTranslationCQL(q), [q.query, q.filters]);

  // ── Fetch translation keys ────────────────────────────────────────
  const { data: keysData, isLoading } = useQuery(
    ['translation-keys', CATEGORY, cql, q.offset],
    () => ky.get('translation-keys', {
      searchParams: { query: cql, offset: q.offset, limit: PAGE_SIZE },
    }).json(),
    { enabled: !!localeValue, keepPreviousData: true },
  );

  // ── Stats — provides apps + tables + counts for filters ─────────────
  const { data: statsData } = useQuery(
    ['translation-keys-stats', CATEGORY, localeValue],
    () => ky.get('translation-keys/stats', {
      searchParams: { localeValue, categoryName: CATEGORY },
    }).json(),
    { enabled: !!localeValue, staleTime: 60_000 },
  );

  const apps = useMemo(() => (
    (statsData?.byApp || []).map((a) => a.appName).filter(Boolean).sort()
  ), [statsData]);

  const tables = useMemo(() => (
    (statsData?.byTable || [])
      .filter((t) => !appName.length || appName.includes(t.appName))
      .map((t) => t.tableName)
      .filter(Boolean)
      .filter((v, i, arr) => arr.indexOf(v) === i)
      .sort()
  ), [statsData, appName]);

  const onNeedMoreData = (_ask, index) => {
    const newOffset = index >= 0 ? index : q.offset + PAGE_SIZE;
    history.push(buildUrl(location, { offset: newOffset }));
  };

  return (
    <TranslationWrapper
      data={{ translationKeys: keysData?.translationKeys || [], totalCount: keysData?.totalRecords || 0, isLoading }}
      onNeedMoreData={onNeedMoreData}
      queryGetter={queryGetter}
      querySetter={querySetter}
      initialSearch={`?filters=localeValue.${localeValue || ''}`}
      languages={languages}
      apps={apps}
      tables={tables}
      categoryName={CATEGORY}
      paneTitle={intl.formatMessage({ id: 'ui-translations.category.libTranslations' })}
      activeFilterKeys={['locale', 'app', 'table', 'status']}
      isAssignedTranslator={isAssignedTranslator(localeValue)}
      mutator={{ resultOffset: { replace: (v) => history.push(buildUrl(location, { offset: v })) } }}
      resources={{}}
    />
  );
}

LibTranslationsRoute.propTypes = {
  history:  PropTypes.shape({ push: PropTypes.func.isRequired, replace: PropTypes.func.isRequired }).isRequired,
  location: PropTypes.object.isRequired,
};

export default LibTranslationsRoute;
